import { JSX, PureComponent } from "react";
import { Link, Route, RouteComponentProps, Switch } from "react-router-dom";
import { NotFound } from "@app/shared/ErrorPages";
import { getViewNameFromKey, ProvidersResult } from "@app/shared/data-summaries";
import { SummaryResultNetworkType, SummaryResultScope, SummaryResultType } from "@app/shared/data-summaries/enumerations";
import { SortDirections } from "@app/shared/waffles";
import { ProviderListing } from "@app/shared/data-summaries/ProviderListing";
import { SummaryDetails } from "@app/projects/network-assessment/summary/SummaryDetails";
import SummaryService from "@app/projects/network-assessment/summary/summaryService";

/** Renders the routing components for the summary portion when looking at a project. */
export default function SummaryRouter(props: RouteComponentProps): JSX.Element {

  return (
    <Switch>
      <Route exact={true} path={`${props.match.path}`} component={SummaryDetails} />
      <Route exact={true} path={`${props.match.path}/:resultType`} component={SummaryDetails} />

      <Route path={`${props.match.path}/:runConfigurationId/:scope/:networkType/:resultType/:key`} component={ParameterExtractor} />
      <Route component={NotFound} />
    </Switch>
  );
}

/** Extracts components from the routes to pass to the ProviderListings. */
class ParameterExtractor extends PureComponent<RouteComponentProps<{ key: string; networkType: string; projectId: string; runConfigurationId: string; scope: string; resultType: string; }>> {
  private readonly summaryService = new SummaryService();

  public render() {
    return <>
      <Link className="qes-button qes-button-secondary" type="button" id="summaries__back" to={`/projects/${this.projectId}/summary/${getViewNameFromKey(this.resultType).replace(/ /g, "")}`}>Go Back to Summaries &quot;{getViewNameFromKey(this.resultType)}&quot;</Link>
      {this.renderDrilldown()}
    </>;
  }

  /** Selects and renders the correct drilldown, otherwise falls back to NotFound. */
  private renderDrilldown() {

    const exportEndpoint = `${this.props.match.url}/export`;

    return <ProviderListing
      networkType={this.networkType}
      getProviders={this.getProviders}
      projectId={this.projectId}
      runConfigurationId={this.runConfigurationId}
      scope={this.scope} resultType={this.resultType}
      summaryKey={this.summaryKey}
      exportEndpoint={exportEndpoint}
      projectURL={this.props.location.pathname + this.props.location.search}
    />;
  }

  /** The matched project identifier from the route. */
  private get projectId() {
    return Number(this.props.match.params.projectId);
  }

  /** Retrieves the network type from the queryParams. */
  private get networkType() {
    return this.props.match.params.networkType as SummaryResultNetworkType;
  }

  /** The type of results being retrieved. */
  private get resultType() {
    return this.props.match.params.resultType as SummaryResultType;
  }

  /** The unique summary key for the drilldown request. */
  private get summaryKey() {
    return this.props.match.params.key;
  }

  /** The run configuration. */
  private get runConfigurationId(): number {
    return Number(this.props.match.params.runConfigurationId);
  }

  private get scope() {
    return this.props.match.params.scope as SummaryResultScope;
  }

  /** Represents the function needed to fetch providers. */
  private getProviders: (skip: number, searchValue: string, sortKey: string, sortDirection: SortDirections) => Promise<ProvidersResult> = async (skip = 0, searchValue: string, sortKey: string, sortDirection: SortDirections) => {
    return await this.summaryService.getProviders(this.projectId, this.runConfigurationId, this.scope, this.networkType, this.resultType, this.summaryKey, skip, searchValue, sortKey, sortDirection);
  }
}
